import React, { FC, useEffect, useState } from 'react';
import { Form as AntdForm, Button } from 'antd';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Popconfirm } from 'antd';
import EmailValidator from 'email-validator';
import { useRedux } from 'use-redux';
import { I18n, Translate } from 'react-redux-i18n';

import { addError } from '@actions/utils';
import Form from '@ui/Form';
import InputFormField from '@ui/Form/Fields/Input';
import { BEMHelper } from '@utils/customBEMHelper';
import { movePayment } from '@api/payment';
import { checkUserRegister } from '@api/index';
import { IMovingPaymentProps } from './interfaces';
import Schema from './schema';

import './styles.scss';

const classes = new BEMHelper({
  name: 'moving-payment-form',
});

const MovingPaymentForm: FC<IMovingPaymentProps> = ({ payment, onSubmit }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConfirmVisible, setIsConfirmVisible] = useState<boolean>(false);
  const [isValidEmail, setIsValidEmail] = useState<boolean>(false);
  const [isRegisterChecked, setIsRegisterChecked] = useState<boolean>(false);
  const [isRegistered, setIsRegistered] = useState<boolean>(false);

  const [, dispatch] = useRedux();

  const initialValues = {
    email: '',
  };

  const formMethods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(Schema),
  });

  const { handleSubmit, watch, reset } = formMethods;

  const email = watch('email');

  const onFinish = async ({ email }) => {
    setIsLoading(true);
    try {
      const res = await movePayment({ email, paymentId: payment.id });
      reset();
      onSubmit(res);
    } catch (error) {
      dispatch(addError(error.response.body));
    }
    setIsLoading(false);
  };

  const toggleConfirmVisible = () => setIsConfirmVisible(prev => !prev);

  const UserNotRegisteredConfirm = () => (
    <span>
      <Translate
        dangerousHTML
        value="admin.paymentsStats.userNotRegisteredConfirm"
        email={email}
      />
    </span>
  );

  const UserRegisteredConfirm = () => (
    <span>
      <Translate
        dangerousHTML
        value="admin.paymentsStats.userRegisteredConfirm"
        email={email}
      />
    </span>
  );

  const userRegisteredLabel = () => {
    if (isValidEmail && isRegistered) {
      return <span>{I18n.t('admin.paymentsStats.userExists')}</span>;
    } else if (isValidEmail && !isRegistered) {
      return <span>{I18n.t('admin.paymentsStats.userNotExists')}</span>;
    }

    return null;
  };

  // Проверяем валидность email
  useEffect(() => {
    setIsValidEmail(EmailValidator.validate(email));
  }, [email]);

  // Проверяем, зарегистрирован ли пользователь
  useEffect(() => {
    (async () => {
      if (!isValidEmail) return;
      setIsRegisterChecked(true);
      const { isRegistered } = await checkUserRegister({ email });
      setIsRegisterChecked(false);
      setIsRegistered(isRegistered);
    })();
  }, [isValidEmail, email]);

  return (
    <Form formMethods={formMethods} onSubmit={onFinish} className={classes()}>
      <InputFormField
        name="email"
        label="Email"
        className={classes('field-input')}
        disabled={isLoading}
        help={userRegisteredLabel()}
      />

      <AntdForm.Item>
        <Popconfirm
          placement="right"
          visible={isConfirmVisible}
          title={
            <>
              {!isRegistered && <UserNotRegisteredConfirm />}
              <UserRegisteredConfirm />
            </>
          }
          okButtonProps={{
            onClick: () => {
              handleSubmit(onFinish)();
              toggleConfirmVisible();
            },
          }}
          cancelButtonProps={{
            onClick: () => {
              toggleConfirmVisible();
            },
          }}
          okText={I18n.t('admin.paymentsStats.move')}
          cancelText={I18n.t('main.buttons.cancel')}
          disabled={isLoading || !isValidEmail}
        >
          <Button
            type="primary"
            onClick={toggleConfirmVisible}
            disabled={isLoading || isRegisterChecked || !isValidEmail}
          >
            {I18n.t('admin.paymentsStats.movePayment')}
          </Button>
        </Popconfirm>
      </AntdForm.Item>
    </Form>
  );
};

export default MovingPaymentForm;
